import {UserSharedAccountData} from './dashboard-clinic-patients.symbols';

/** Describes available options for loading API to sort shared patients. */
export enum ClinicPatientsSortRule {
  /** Sort by patient's name ascending. */
  NameAscend = 'name_ascend',

  /** Sort by patient's name descending. */
  NameDescend = 'name_descend',

  /**
   * Sort by patient scan dates ascending (when the MRI was taken).
   * The patient list will be formed depend on each patient's latest scan.
   */
  ScanDateAscend = 'scan_date_ascend',

  /**
   * Sort by patient scans dates ascending (when the MRI was taken).
   * The patient list will be formed depend on each patient's earliest scan.
   */
  ScanDateDescend = 'scan_date_descend',

  /**
   * Sort by patient scan dates ascending (when the MRI was uploaded).
   * The patient list will be formed depend on each patient's latest scan.
   */
  UploadDateAscend = 'upload_date_ascend',

  /**
   * Sort by patient scans ascending (when the MRI was uploaded).
   * The patient list will be formed depend on each patient's earliest scan.
   */
  UploadDateDescend = 'upload_date_descend',
}

/**
 * Describes all available options for filtering shared patients.
 */
export interface ClinicPatientsAppliedFilters {
  /** Filtering by results inspected field. */
  results_inspected: ClinicPatientsResultsInspectedFilter;
}

/**
 * Describes all available options for filtering by result inspected field.
 */
export enum ClinicPatientsResultsInspectedFilter {
  /** Do not filter by result inspected field. */
  notApplied = 'not_applied',

  /** Show only patients with results inspected. */
  inspected = 'inspected',

  /** Show only patients with results not inspected. */
  notInspected = 'not_inspected',
}

/**
 * Default values for applied filters.
 */
export const ClinicPatientsAppliedFilterDefault: ClinicPatientsAppliedFilters = {
  results_inspected: ClinicPatientsResultsInspectedFilter.notApplied,
};

/** Define the number of patient accounts that should be presented on the single page. */
export const PATIENTS_ACCOUNTS_ON_PAGE = 12;

/**
 * Describes data structure of shared accounts to the user after applying filters.
 */
export interface FilteredSharedAccounts {
  /** The total number of items matched provided filters. */
  count: number;

  /** Contain first `PAGE_SIZE` items matched provided filters. */
  results: UserSharedAccountData[];
}

/**
 * Describes all available sorting rules for the patient list.
 * - asc - ascending
 * - desc - descending
 */
export type PatientSortingRules = 'asc' | 'desc';

/**
 * Describes all available fields for which the sorting can be applied.
 */
export interface AppliedSortingFields {
  /** Sorting by patient's name. */
  name_sorting?: PatientSortingRules;

  /** Sorting by patient's scan date. */
  scan_date?: PatientSortingRules;

  /** Sorting by patient's scan upload date. */
  upload_date?: PatientSortingRules;
}

/**
 * Describes all available fields for which the filtering can be applied.
 */
export interface AppliedFilteredFields {
  /**
   * Filtering by results inspected field.
   * - true - show only patients with results inspected
   * - false - show only patients with results not inspected
   * - not provided - show all patients
   */
  results_inspected?: boolean;
}

/**
 * Describes payload for LoadPatientAccountsSuccess action.
 */
export interface PayloadLoadPatientAccountsSuccess {
  /** Loaded accounts data for current params. */
  filteredAccounts: FilteredSharedAccounts;

  /** Current search string. */
  searchString: string;

  /** Current page. */
  selectedPage: number;

  /** Applied sorting rules. */
  sortingRule: ClinicPatientsSortRule;

  /** Applied filtering rules. */
  filters: ClinicPatientsAppliedFilters;
}
